/**
 * Every number both the server and the browser must agree on.
 *
 * Anything that appears on the wire, or that one side uses to predict what the
 * other side is doing, belongs here rather than beside the code that reads it.
 * Two copies of a tick rate is two copies that can drift, and the symptom of
 * that drift is not a crash -- it is cats that stutter for reasons nobody can
 * reproduce.
 *
 * This file has no imports on purpose.  It is the bottom of the dependency
 * graph, so a constant is always safe to reach for.
 */
/**
 * Wire format generation -- the newest dialect this build speaks, and the
 * top of the compatibility window the server serves (docs/ARCH-PLAN.md
 * §4.3).  Bumped when a message changes shape in a way an older client
 * cannot read.
 *
 * That should almost never happen, and the reason is the discipline already
 * running through this file: every wire enum below (STATES, PREY_SPECIES,
 * PREY_MODES, DOG_MODES, FIND_STATE) is append-only forever, and messages
 * may gain fields but never lose, renumber or repurpose one.  A client one
 * deploy behind therefore reads the new wire minus the additions -- a
 * phrase not shown, a sky that eases in late -- and the window is free.
 * Keep every change additive and NEITHER of these constants moves; bump
 * PROTO_VERSION only for a change an old client genuinely cannot read, and
 * raise PROTO_MIN only when serving an old dialect has become untrue --
 * that second move strands every un-updated client, so it is taken out
 * loud, never in passing.
 *
 * The window policy is itself append-only (§7.1): both numbers only ever
 * rise, and a generation once spent is never reused for something else.
 * A join/hello carries the client's `v`; the server admits anything in
 * [PROTO_MIN, PROTO_VERSION] and refuses the rest with CLOSE.PROTO and the
 * window in the deny, so the far end can tell the player something they
 * can act on.  A hello with NO `v` at all is a client minted before
 * negotiation existed -- every one of those speaks v1, which is why v1
 * stays in the window until raising PROTO_MIN says otherwise.  None of
 * this can ever brick a shipped desktop build: the binary carries its own
 * matching server, so an out-of-window deny gates the VISIT to the public
 * street, never the game (§4.3).
 */
export const PROTO_VERSION = 1;
/** The oldest dialect still served -- the bottom of the window.  1 since
    before negotiation existed, and it only ever rises (see above). */
export const PROTO_MIN = 1;
/**
 * Server simulation rate.
 *
 * 20 Hz, not BreachProtocol's 30.  That game rewinds player positions to
 * resolve a bullet and its whole hit-registration budget is measured in
 * milliseconds; nothing here is ever decided on a frame boundary.  A cat
 * walks at 0.62 m/s and sprints at 2.4, so a 50 ms tick moves it 31 mm at a
 * walk and 120 mm at a run -- comfortably inside the interpolation window on
 * the far end, and 16 cats' worth of physics and pathing at 20 Hz leaves the
 * single core the pod is limited to mostly idle.
 */
export const TICK_RATE = 20;
/** Snapshots per tick.  1 = every tick.  At 16 cats a snapshot is ~700 bytes,
    so 20 Hz costs ~14 KB/s per client -- less than one of the game's own
    texture canvases, and well under what the ingress will notice. */
export const SNAP_EVERY = 1;
/** How often the browser reports its own cat's pose.  Matched to the tick so
    the server has exactly one fresh pose per cat per tick: faster is wasted
    (the extra frames are overwritten unread), slower leaves ticks where a
    human-driven cat did not move and the far end interpolates through a
    stutter it invented. */
export const STATE_SEND_MS = 50;
/** The whole point of the roster cap: the neighbourhood is three houses and a
    wood, and seventeen cats in it is a cat infestation rather than a street. */
export const MAX_CATS = 16;
/**
 * How far in the past remote cats are rendered.
 *
 * A client draws other cats between the two snapshots that bracket
 * `now - delay`, so the delay has to exceed the gap between snapshots or there
 * is nothing to interpolate toward and the motion falls back to the last known
 * pose -- which reads as a cat teleporting once per dropped packet.  The
 * adaptive delay in net/interp.js rides between these two bounds.
 */
export const INTERP_MIN = 0.08; // s -- 1.6 snapshots at 20 Hz
export const INTERP_MAX = 0.30; // s -- past this a cat is visibly behind
/** Multiplier on measured jitter when widening the interpolation delay.  Same
    value BreachProtocol landed on: below about 2 the delay tracks the median
    and every above-median packet arrives late. */
export const INTERP_JITTER_MULT = 2.5;
/** Application close codes.  4000-4999 is the range WebSocket reserves for
    the application, and a numbered close is the difference between "the
    server hung up on you for a reason" and "the network died". */
export const CLOSE = {
    FLOOD: 4008, // the socket would not stop talking
    REPLACED: 4009, // the same token joined from somewhere else
    PROTO: 4010, // the hello's version fell outside [PROTO_MIN,
    // PROTO_VERSION]; the deny sent just before this
    // close carries the window (protocol.ts)
    FULL: 4011, // the street had no bed (the deny just before this
    // said why).  Exists so a refused socket is CLOSED
    // rather than left subscribed to every broadcast --
    // measured at 19.1 KiB/s of world feed to a visitor
    // who was told there is no room (2026-09-01).  Also
    // the goodbye for a socket that sat on the join card
    // past JOIN_GRACE_MS without claiming (index.js) --
    // the same class of exit, no bed was taken, with its
    // own reason string.
};
/** Biggest frame the server will accept.  A `look` is ~25 numbers and a name
    is 24 characters; nothing legitimate is close to this.  The cap exists so
    a hostile client cannot make the server allocate before it can be judged. */
export const WS_MAX_PAYLOAD = 16 * 1024;
/** Sustained and burst message rates per socket, per second.  A playing
    client sends 20 pose frames a second and a ping every two; 60 leaves an
    order of magnitude of headroom for a customizer being dragged. */
export const MSG_RATE = 60;
export const MSG_BURST = 120;
/** Longest a cat name may be.  Matches the customizer's own cap so a name
    typed in the builder and a name sent at join cannot disagree. */
export const NAME_MAX = 24;
/**
 * The cat's state vocabulary, in wire order.
 *
 * player.js returns these as strings; a snapshot carries the index.  The array
 * is append-only forever -- an index is a promise to every client already
 * running, and reordering it would silently turn every sitting cat into a
 * pouncing one.
 */
export const STATES = [
    "idle", "walk", "run", "jump", "fall", "land",
    "sit", "stalk", "swipe", "pounce", "cling",
];
export const STATE_INDEX = Object.fromEntries(STATES.map((s, i) => [s, i]));
/** Snapshot row flags. */
export const Flag = {
    HUMAN: 1, // somebody is at the wheel; otherwise the brain is
    PURR: 2, // happy enough to be heard
};
/**
 * Where the purr starts, and where the meter reads full.
 *
 * PURR_AT is below 1 deliberately.  A cat that only purrs at a perfect score
 * purrs approximately never -- the wants regrow continuously, so the top of
 * the meter is a moment, not a state.  At 0.72 a cat that has been looked
 * after purrs most of the time and stops when it has been neglected, which is
 * the feedback the mechanic is actually made of.
 */
export const PURR_AT = 0.72;
/**
 * How long a cat has to stay in a happy place for it to count.
 *
 * Long enough that walking through the creek on the way somewhere else does
 * not bank it -- the visit has to be the point -- and short enough that a
 * player who went there on purpose is not waiting on a progress bar.
 */
export const DWELL = 6.0; // seconds inside the zone
/**
 * How fast a want regrows, in units per second, from nothing to full.
 *
 * 1/540 is a nine-minute regrow.  Ten places means a cat left entirely alone
 * empties out over about the same nine minutes; a cat being played reaches
 * every place it can in far less, which is what makes the meter feel like
 * something you are keeping up rather than something you filled in once.
 */
export const WANT_REGROW = 1 / 540;
/**
 * The prey vocabulary, in wire order.  Same append-only contract as STATES:
 * a snapshot row carries the index, so reordering either array would turn
 * every carried mouse into a fleeing bird on any client one deploy behind.
 *
 * Two species, not a per-critter bestiary, because the wire and the record
 * both key on the species name: mice live by the shed, birds on the front
 * lawn, and the collection tally counts each.  MODES is the server's whole
 * behaviour alphabet -- the client renders a mode, never simulates one, so
 * anything not expressible in this list does not exist on screen.
 *
 *   lurk     sitting tight (a mouse under cover, a bird pecking)
 *   dart     moving to a new spot of its own accord
 *   flee     it has noticed a cat and is getting away
 *   carried  in a cat's mouth; the row's CARRIER column says whose
 *   gone     caught, delivered or escaped -- waiting to respawn, not drawn
 */
export const PREY_SPECIES = ["mouse", "bird"];
export const PREY_MODES = ["lurk", "dart", "flee", "carried", "gone"];
/**
 * Quick-chat pacing, per CAT (not per socket -- a reconnect must not refill
 * the bucket; see the `say` case in server/src/index.js).
 *
 * Chat here is a phrase book, not a keyboard, so the budget is sized for
 * conversation rather than typing: SAY_BURST of 3 covers the natural opening
 * volley ("Hi!", "Follow me!", "Race you to the creek!") without a pause,
 * and half a phrase per second sustained -- one every two seconds -- is as
 * fast as anyone can read bubbles over a moving cat anyway.  Anything past
 * that is one cat wallpapering the street, and the server silently DROPS the
 * overflow rather than closing the socket: a kid mashing the talk button is
 * the expected user, not an attacker, and the SocketMeter still guards the
 * socket itself.  Fractional rates are fine for the token bucket's
 * arithmetic (limits.js refills continuously, not per second).
 *
 * Shared rather than server-only because the client runs a mirror of the
 * bucket (game/src/chat.js): the menu refuses a phrase the server would
 * drop, so what the player sees themselves "say" is always what everyone
 * else saw too.
 */
export const SAY_RATE = 0.5;
export const SAY_BURST = 3;
/**
 * The treasure-find state vocabulary, in wire order.  The `finds` message
 * (protocol.js) carries one of these per shared/happy.js FINDS row, to the
 * owner only -- which treasure another cat has found is that cat's business,
 * the same privacy line wireRecord draws around the wants.
 *
 * Three states rather than a found/not bit because the client needs to
 * answer two different questions from one number: "do I draw it?" (OUT and
 * BACK yes, TAKEN no) and "is it in my collection?" (TAKEN and BACK yes,
 * OUT no).  Collapsing either pair loses an answer.
 *
 *   OUT     never found by this cat; hidden out there, drawn, findable
 *   TAKEN   found by this cat and still re-hiding; in the collection, not drawn
 *   BACK    found before, and the re-hide clock has run out: drawn and
 *           findable again (for the want -- the collection already has it)
 *
 * Same append-only contract as STATES and PREY_MODES: these are wire
 * indexes, and reordering them would turn every collected treasure back
 * into an undiscovered one on any client a deploy behind.
 */
export const FIND_STATE = { OUT: 0, TAKEN: 1, BACK: 2 };
/**
 * How long one full day lasts, in real seconds.
 *
 * 20 minutes.  The number is sized against a play session, not against a
 * planet: a kid plays for ten or twenty minutes, and a day this long means
 * every session crosses at least one of the good bits -- a sunset, a
 * moonrise, a dawn -- without ever parking them in the dark for longer than
 * a few minutes (night is ~44% of the cycle, so worst case is about nine
 * minutes of picture-book moonlight).  Much shorter and the sun visibly
 * crawls, which turns the sky into a screensaver; much longer and a player
 * who only ever plays after school only ever sees one hour of it.
 */
export const DAY_LENGTH_S = 1200;
/**
 * The time of day, as a phase in [0, 1): 0.0 is midnight, 0.5 is noon.
 *
 * A function rather than a constant, but it lives here because it is exactly
 * what this file's header promises: a thing both ends must compute the SAME
 * way.  The server stamps the street's sky with it and the client uses the
 * identical formula as its first guess before the server's word arrives, so
 * one copy is the difference between a sky that is right on the first frame
 * and a sky that visibly snaps a second after the page loads.
 *
 * Anchored to the WALL CLOCK, and that choice is the whole feature.  The
 * server already keeps `world.time`, but that is process uptime, and the
 * deploy strategy is Recreate -- the process restarts on every single ship,
 * and a cycle anchored to uptime snaps the whole street back to the same
 * hour every time we deploy.  Epoch time flows straight through a restart,
 * so a deploy costs the players a two-second hiccup and not an eclipse.
 * The cost of wall clock is NTP: a step moves the sky by the size of the
 * step, which for a cosmetic cycle eased on the client is nothing -- the
 * same trade world.js refuses for the SIMULATION clock, where a step would
 * expire sixteen brains' deadlines at once, is harmless for a sunset.
 */
export const dayPhase = (epochMs) => ((epochMs / 1000) % DAY_LENGTH_S) / DAY_LENGTH_S;
/**
 * The dog's behaviour alphabet, in wire order.  Same append-only contract
 * as STATES and PREY_MODES: the snapshot row carries the index, so the
 * array may grow but never reorder.
 *
 * The client renders a mode, never simulates one -- the dog is
 * server-authoritative end to end (server/src/dog.js), and these four
 * words are its entire public behaviour:
 *
 *   patrol   trotting its road between sniff stops
 *   sniff    stationary, nose down at the tarmac (also how it catches its
 *            breath after a chase that came to nothing)
 *   chase    it has picked a cat in the road and is coming, tail up
 *   woof     it tagged one: planted, barking, delighted -- the startled
 *            cat's guaranteed window to get away clean
 */
export const DOG_MODES = ["patrol", "sniff", "chase", "woof"];
/**
 * Burrs.  A handful of bushes around the level are dried out and studded
 * with seed heads, and a cat that walks into one gets tangled: a few seconds
 * of wriggling free, and (for a driven cat) a sting of happiness.  The skill
 * is avoiding them, which is why every burred bush is drawn as the SAME
 * rusty, spiky plant everywhere it appears (level modules call greenspace's
 * burrBush and nothing else) -- one silhouette to learn, learnable at a
 * glance from across a garden.
 *
 * The numbers live here because the mechanic is judged twice from one set of
 * facts: game/src/player.js runs the tangle (on the browser's cat and, via
 * the server's physics, on every AI cat), and server/src/burrs.js judges the
 * sting from the pose the server accepted.  Two copies of any of these and
 * the two ends disagree about whether a cat is in a bush, which is the drift
 * constants.js exists to prevent.
 */
/**
 * The snag disc, in plan metres, around each marked bush.  Deliberately
 * inside the ~0.7 m of foliage the bush is drawn with, so the trap is always
 * smaller than the thing that warns of it: brushing the outer leaves is a
 * near miss, standing in the plant is a snag.  The stepping stones make the
 * same promise from the other side -- their colliders are narrower than the
 * stones so nothing is standable that does not look standable; here nothing
 * is snaggable that does not look snaggable.
 */
export const BURR_R = 0.55;
/**
 * The height band, measured from the bush's own ground.  A snag needs the
 * paws in the plant, not the cat sailing over it: a sprint jump entering the
 * disc is already ~0.55 m up by the time it crosses the centre (0.55 m of
 * approach at 2.4 m/s under a 4.29 m/s launch), so 0.45 lets any honest leap
 * clear the bush while a walk, a stalk, or a shallow landing never does.
 * The 0.25 below forgives judging a paw from a dip beside the bush's base;
 * deeper than that is the creek bed, and no burrs grow in the water.
 */
export const BURR_Y_UP = 0.45;
export const BURR_Y_DOWN = 0.25;
/**
 * How long the tangle lasts with nobody wriggling.  This is the whole
 * penalty an AI cat can ever pay -- brains hold keys, they do not mash --
 * so it is sized against the machinery that watches them: well under the
 * brains' 12 s lost clock and the soak's stillness window, and just long
 * enough to be an event rather than a stutter.  The timer runs down
 * unconditionally every frame, which is the structural guarantee that a
 * snagged cat ALWAYS frees itself: there is no code path that pauses it.
 */
export const SNAG_TIME = 2.8;
/**
 * Seconds knocked off the tangle by each fresh press of jump.  A determined
 * mash (~5 presses a second) is free in about one second -- roughly three
 * times faster than waiting, so the wriggle is genuinely worth doing --
 * and the passive SNAG_TIME above stays the worst case for everyone.
 * Presses, not holds: holding Space is a decision made once, and a struggle
 * should cost struggle.
 */
export const SNAG_MASH = 0.35;
/**
 * Top speed while tangled, m/s.  Not zero on purpose: the design rule is a
 * struggling cat, never a frozen cat and a dead controller, so the stick
 * still drags the body a visible strain's worth.  Under the 0.26 stalk by
 * enough that "tangled" reads slower than any gait the cat owns, and small
 * enough that 2.8 s of it (0.28 m) cannot creep OUT of a 0.55 m snag disc
 * -- escaping is the timer's job, not the throttle's.
 */
export const SNAG_CREEP = 0.10;
/**
 * Seconds of immunity after pulling free.  At a 0.62 m/s walk this is 3.7 m
 * of protected travel -- several bush-widths -- so getting free is always
 * real: neither a player nor a brain can be re-snagged before they are
 * genuinely clear of the plant they just escaped.  This is also the other
 * half of the AI guarantee: a brain holding a straight-line course through
 * a bush snags once, times out, and walks out the far side inside the
 * immunity, whatever its path was.
 */
export const SNAG_IMMUNE = 6.0;
/**
 * The sting: every want regrows by this fraction of the progress it held,
 * which drops happiness multiplicatively by exactly this factor (happiness
 * is one minus the mean want, so scaling every want's emptied part scales
 * the whole meter).  An eighth costs a well-kept cat about one place-visit
 * of care (a full visit pays 1/12 of the meter) -- a sting, not a session
 * undone -- and it can pull a cat purring at 0.78 back under PURR_AT, which
 * is the feedback: the purr stopping says "that cost you" louder than any
 * number.  Expressed as regrown wants rather than a score deduction because
 * there IS no score -- wants that regrow are the only currency this game
 * has, and a burr is a few minutes of neglect happening all at once.
 */
export const BURR_STING = 0.125;
/**
 * Server-side re-sting guard, seconds.  The sting fires on the transition
 * into a bush and this cooldown besides, comfortably past SNAG_TIME, so one
 * blunder is one sting even when the wriggle drags the accepted pose across
 * the snag rim and back.  Shorter than SNAG_TIME + SNAG_IMMUNE on purpose:
 * by the time it expires the client-side immunity has already walked the
 * cat clear, so the pair can never double-charge an honest player.
 */
export const BURR_COOLDOWN = 8.0;
